import { useEffect, useState } from "react";
import { CheckCircle2, HandCoins, History } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatPrice } from "@/lib/api";

export default function SchoolCommissions() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/admin/ecoles-commissions").then(({ data }) => setRows(data.schools)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const payout = async (s) => {
    const note = window.prompt(`Marquer ${formatPrice(s.remaining_cents)} comme versés à ${s.name} ?\nNote (chèque n°, virement…) — facultatif :`, "");
    if (note === null) return;
    try { const { data } = await api.post(`/admin/ecoles/${s.school_id}/commissions/payout`, { note }); toast.success(data.message); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!rows || rows.length === 0) return null;
  const totalRemaining = rows.reduce((t, r) => t + Math.max(r.remaining_cents, 0), 0);

  return (
    <div data-testid="school-commissions" className="rounded-2xl border border-amber-800/15 bg-white p-5 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-serif text-lg font-semibold text-bark"><HandCoins size={18} className="text-honey" /> Commissions à reverser</p>
        <p data-testid="commissions-total-remaining" className="text-sm text-bark-soft">Reste à verser : <strong className="text-terra">{formatPrice(totalRemaining)}</strong></p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead><tr className="border-b border-amber-900/10 text-xs uppercase tracking-wider text-stone-mute">
            <th className="py-2 pr-3">École</th><th className="py-2 pr-3 text-right">Due</th><th className="py-2 pr-3 text-right">Versée</th><th className="py-2 pr-3 text-right">Reste</th><th className="py-2 text-right">Action</th>
          </tr></thead>
          <tbody className="divide-y divide-amber-900/10">
            {rows.map((s) => (
              <tr key={s.school_id} data-testid={`commission-row-${s.school_id}`} className="align-top">
                <td className="py-2.5 pr-3">
                  <p className="font-medium text-bark">{s.name}</p>
                  {s.payouts.length > 0 && (
                    <button data-testid={`commission-history-${s.school_id}`} onClick={() => setOpen(open === s.school_id ? null : s.school_id)} className="mt-0.5 flex items-center gap-1 text-xs text-bark-soft hover:text-amber-deep"><History size={12} /> {s.payouts.length} versement(s)</button>
                  )}
                  {open === s.school_id && (
                    <ul className="mt-1 space-y-0.5 text-xs text-bark-soft">
                      {s.payouts.map((p) => <li key={p.payout_id}>{new Date(p.created_at).toLocaleDateString("fr-FR")} · {formatPrice(p.amount_cents)}{p.note ? ` · ${p.note}` : ""}</li>)}
                    </ul>
                  )}
                </td>
                <td className="py-2.5 pr-3 text-right text-bark">{formatPrice(s.due_cents)}</td>
                <td className="py-2.5 pr-3 text-right text-sage">{formatPrice(s.paid_cents)}</td>
                <td data-testid={`commission-remaining-${s.school_id}`} className={`py-2.5 pr-3 text-right font-semibold ${s.remaining_cents > 0 ? "text-terra" : "text-bark-soft"}`}>{formatPrice(s.remaining_cents)}</td>
                <td className="py-2.5 text-right">
                  {s.remaining_cents > 0 ? (
                    <button data-testid={`commission-payout-${s.school_id}`} onClick={() => payout(s)} className="rounded-full bg-sage px-3 py-1.5 text-xs font-semibold text-white hover:bg-sage/90">Marquer comme versée</button>
                  ) : <span className="inline-flex items-center gap-1 text-xs text-sage"><CheckCircle2 size={13} /> À jour</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
